import React, { useState } from 'react';
import { usePrivy, useLogin, useWallets, useLinkAccount, useCreateWallet, useCrossAppAccounts } from '@privy-io/react-auth';
import { useAccount, useDisconnect } from 'wagmi';
import styled from 'styled-components';
import { useZNSName } from '../hooks/useZNSName';
import { useENSName } from '../hooks/useENSName';

// Beholder (Kraken) App ID for Global Wallet integration
const BEHOLDER_APP_ID = import.meta.env.VITE_BEHOLDER_APP_ID;

// Props for WalletConnect component
interface WalletConnectProps {
  /** Use ENS (.eth) instead of ZNS (.ink) for domain resolution. Default: false (uses ZNS) */
  useENS?: boolean;
}

const SignInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  cursor: pointer;
  border: none;
  outline: none;
  font-weight: 600;
  font-size: 14px;
  min-width: 100px;
  height: 42px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
    background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;

const UserBadgeContainer = styled.div`
  position: relative;
`;

const UserBadge = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 10px;
  background: rgba(16, 185, 129, 0.15);
  border: 1px solid rgba(16, 185, 129, 0.3);
  color: #10b981;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: rgba(16, 185, 129, 0.2);
    border-color: rgba(16, 185, 129, 0.5);
  }
`;

const WalletDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 320px;
  background: var(--color-bg-elevated, #111111);
  border: 1px solid var(--color-border, #1f1f1f);
  border-radius: 12px;
  padding: 16px;
  z-index: 1000;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.6);
`;

const DropdownHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border, #1f1f1f);
`;

const DropdownTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const LogoutButton = styled.button`
  background: transparent;
  border: none;
  color: #71717a;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
  }
`;

const WalletSection = styled.div`
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const WalletLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 500;
  color: #71717a;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const WalletChainIcon = styled.img`
  width: 14px;
  height: 14px;
  border-radius: 50%;
`;

const WalletAddress = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--color-bg, #0a0a0a);
  border: 1px solid var(--color-border, #1f1f1f);
  border-radius: 8px;
  font-family: 'IBM Plex Mono', monospace;
  font-size: 12px;
  color: #ffffff;
`;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: #52525b;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.15s ease;
  
  &:hover {
    color: #10b981;
    background: rgba(16, 185, 129, 0.1);
  }
`;

const NoWalletText = styled.div`
  font-size: 12px;
  color: #52525b;
  padding: 8px 12px;
  background: var(--color-bg, #0a0a0a);
  border: 1px dashed var(--color-border, #1f1f1f);
  border-radius: 8px;
  text-align: center;
`;

const UserInfo = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border, #1f1f1f);
`;

const UserInfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #a1a1aa;
  margin-bottom: 4px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  span:first-child {
    color: #71717a;
  }
`;

const ConnectWalletButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: 1px dashed var(--color-border, #252525);
  border-radius: 8px;
  color: #71717a;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    border-color: #10b981;
    color: #10b981;
    background: rgba(16, 185, 129, 0.05);
  }
`;

const WalletTypeLabel = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgba(168, 85, 247, 0.15);
  color: #a855f7;
  margin-left: 8px;
`;

const WalletSectionTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #52525b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
  margin-top: 4px;
`;

const PrimaryButton = styled.button<{ $isPrimary: boolean }>`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.15s ease;
  color: ${props => props.$isPrimary ? '#fbbf24' : '#3f3f46'};
  
  &:hover {
    color: ${props => props.$isPrimary ? '#fbbf24' : '#a1a1aa'};
    background: ${props => props.$isPrimary ? 'rgba(251, 191, 36, 0.1)' : 'rgba(161, 161, 170, 0.1)'};
  }
`;

const PrimaryBadge = styled.span`
  font-size: 8px;
  padding: 2px 4px;
  border-radius: 3px;
  background: rgba(251, 191, 36, 0.2);
  color: #fbbf24;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.3px;
  margin-left: 4px;
`;

// Helper to format address
const formatAddress = (address: string): string => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

const WalletConnect: React.FC<WalletConnectProps> = ({ useENS: useENSProp = false }) => {
  const { ready, authenticated, user, logout, unlinkWallet } = usePrivy();
  const { wallets } = useWallets();
  const { address: evmAddress, isConnected } = useAccount();
  const { disconnect } = useDisconnect();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [isLinkingBeholder, setIsLinkingBeholder] = useState(false);
  
  const { login } = useLogin({
    onComplete: (user) => {
      console.log('✅ Privy login complete:', user);
    },
    onError: (error) => {
      console.error('❌ Privy login error:', error);
    },
  });

  // Link wallet hook for connecting additional wallets
  const { linkWallet } = useLinkAccount({
    onSuccess: ({ user, linkedAccount }: any) => {
      console.log('✅ Wallet linked successfully:', linkedAccount);
    },
    onError: (error: any) => {
      console.error('❌ Failed to link wallet:', error);
          },
        });

  // Cross-app accounts hook for Beholder wallet linking
  const { linkCrossAppAccount, unlinkCrossAppAccount } = useCrossAppAccounts();

  // Create embedded wallet hook
  const { createWallet } = useCreateWallet();
  const isCreatingWallet = false; // Removed - not available in current Privy version

  // Categorize wallets from useWallets()
  // Note: ConnectedWallet uses 'type' property ('ethereum' | 'solana')
  // Cast to any[] to avoid TypeScript narrowing issues
  const allWallets = wallets as any[];
  const embeddedEvmWalletFromHook = allWallets.find(w => 
    w.walletClientType === 'privy' && w.type === 'ethereum'
  );
  const embeddedSolanaWalletFromHook = allWallets.find(w => 
    w.walletClientType === 'privy' && w.type === 'solana'
  );
  // Get ALL external EVM wallets (not just the first one)
  const connectedEvmWallets = allWallets.filter(w => 
    w.walletClientType !== 'privy' && w.type === 'ethereum'
  );
  const connectedEvmWallet = connectedEvmWallets[0]; // For backwards compatibility
  // Get ALL external Solana wallets
  const connectedSolanaWallets = allWallets.filter(w => 
    w.walletClientType !== 'privy' && w.type === 'solana'
  );
  const connectedSolanaWallet = connectedSolanaWallets[0]; // For backwards compatibility

  // Also check user.linkedAccounts for embedded wallets (Privy v3)
  // Cast to any[] to avoid union type issues with LinkedAccountWithMetadata
  const linkedAccounts = (user?.linkedAccounts || []) as any[];
  
  // Find embedded EVM wallet from linkedAccounts
  const embeddedEvmFromLinked = linkedAccounts.find(account => 
    account.type === 'wallet' && 
    account.walletClientType === 'privy' && 
    (account.chainType === 'ethereum' || account.chainId?.startsWith('eip155'))
  );
  
  // Find embedded Solana wallet from linkedAccounts
  const embeddedSolanaFromLinked = linkedAccounts.find(account => 
    account.type === 'wallet' && 
    account.walletClientType === 'privy' && 
    (account.chainType === 'solana' || account.chainId === 'solana:mainnet')
  );

  // Find Beholder cross-app account (if linked)
  const beholderAccount = linkedAccounts.find((account: any) => 
    account.type === 'cross_app' && 
    account.providerAppId === BEHOLDER_APP_ID
  );

  // Use whichever source has the wallet
  const embeddedEvmWallet = embeddedEvmWalletFromHook || (embeddedEvmFromLinked ? {
    address: embeddedEvmFromLinked.address,
    chainType: 'ethereum' as const,
    walletClientType: 'privy',
  } : null);
  
  const embeddedSolanaWallet = embeddedSolanaWalletFromHook || (embeddedSolanaFromLinked ? {
    address: embeddedSolanaFromLinked.address,
    chainType: 'solana' as const,
    walletClientType: 'privy',
  } : null);

  // Fallback to wagmi for connected EVM wallet
  const externalEvmAddress = connectedEvmWallet?.address || evmAddress || null;
  
  // ===== PRIMARY WALLET SELECTION STATE =====
  // Track which EVM and Solana wallet is selected as primary (declared early for use in ZNS lookup)
  const [primaryEvmWalletAddress, setPrimaryEvmWalletAddress] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('mavrk_primary_evm_wallet');
    }
    return null;
  });

  const [primarySolanaWalletAddress, setPrimarySolanaWalletAddress] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('mavrk_primary_solana_wallet');
    }
    return null;
  });
  
  // Get the user-selected primary EVM address for domain lookup
  // Falls back to embedded wallet if no selection made yet
  const effectivePrimaryEvmAddress = primaryEvmWalletAddress || embeddedEvmWallet?.address || externalEvmAddress || null;

  // Get ZNS name for the user-selected primary EVM wallet (for Ink chain)
  const { znsName } = useZNSName(useENSProp ? undefined : (effectivePrimaryEvmAddress || undefined));
  
  // Get ENS name for the user-selected primary EVM wallet (for Ethereum)
  const { ensName } = useENSName(useENSProp ? (effectivePrimaryEvmAddress || undefined) : undefined);
  
  // Use the appropriate domain name based on chain
  const domainName = useENSProp ? ensName : znsName;

  // Handle Privy login
  const handleLogin = () => {
    if (!ready) return;
    login();
  };

  // Handle connecting external wallet using linkWallet
  const handleConnectExternalWallet = async () => {
    try {
      // linkWallet opens Privy's wallet connection modal
      await linkWallet();
    } catch (err) {
      console.error('Failed to link wallet:', err);
    }
  };

  // Handle linking Beholder wallet via cross-app connect
  const handleLinkBeholder = async () => {
    console.log('🔗 [Beholder] handleLinkBeholder called');
    console.log('🔗 [Beholder] App ID:', BEHOLDER_APP_ID);
    console.log('🔗 [Beholder] linkCrossAppAccount function:', typeof linkCrossAppAccount);
    
    if (!BEHOLDER_APP_ID) {
      console.error('❌ [Beholder] App ID not configured');
      alert('Beholder App ID is not configured. Please check environment variables.');
      return;
    }
    
    if (typeof linkCrossAppAccount !== 'function') {
      console.error('❌ [Beholder] linkCrossAppAccount is not a function:', linkCrossAppAccount);
      alert('Cross-app linking not available. Please check Privy configuration.');
      return;
    }
    
    setIsLinkingBeholder(true);
    try {
      console.log('🔗 [Beholder] Calling linkCrossAppAccount with appId:', BEHOLDER_APP_ID);
      const result = await linkCrossAppAccount({ appId: BEHOLDER_APP_ID });
      console.log('✅ [Beholder] Wallet linked successfully:', result);
    } catch (err: any) {
      console.error('❌ [Beholder] Failed to link wallet:', err);
      console.error('❌ [Beholder] Error message:', err?.message);
      console.error('❌ [Beholder] Error stack:', err?.stack);
      // User might have cancelled - that's ok
      if (err?.message?.includes('cancelled') || err?.message?.includes('closed')) {
        console.log('ℹ️ [Beholder] User cancelled linking');
      } else {
        alert(`Failed to connect Beholder wallet: ${err?.message || 'Unknown error'}`);
      }
    } finally {
      setIsLinkingBeholder(false);
    }
  };

  // Handle unlinking Beholder wallet
  const handleUnlinkBeholder = async () => {
    if (!beholderAccount?.subject) {
      console.error('No Beholder account to unlink');
      return;
    }
    
    try {
      await unlinkCrossAppAccount({ subject: beholderAccount.subject });
      console.log('✅ Beholder wallet unlinked successfully');
    } catch (err) {
      console.error('❌ Failed to unlink Beholder wallet:', err);
    }
  };

  // Track connected Solana wallet locally (for display even if Privy doesn't link it)
  // Persist to localStorage so it survives page refreshes
  const [localSolanaWallet, setLocalSolanaWallet] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('mavrk_local_solana_wallet');
    }
    return null;
  });

  // Save to localStorage when it changes
  const updateLocalSolanaWallet = (address: string | null) => {
    setLocalSolanaWallet(address);
    if (address) {
      localStorage.setItem('mavrk_local_solana_wallet', address);
    } else {
      localStorage.removeItem('mavrk_local_solana_wallet');
    }
  };

  // ===== PRIMARY WALLET HELPER FUNCTIONS =====
  // Update primary EVM wallet
  const setPrimaryEvmWallet = (address: string | null) => {
    setPrimaryEvmWalletAddress(address);
    if (address) {
      localStorage.setItem('mavrk_primary_evm_wallet', address);
      console.log('⭐ Primary EVM wallet set:', address);
    } else {
      localStorage.removeItem('mavrk_primary_evm_wallet');
    }
  };

  // Update primary Solana wallet
  const setPrimarySolanaWallet = (address: string | null) => {
    setPrimarySolanaWalletAddress(address);
    if (address) {
      localStorage.setItem('mavrk_primary_solana_wallet', address);
      console.log('⭐ Primary Solana wallet set:', address);
    } else {
      localStorage.removeItem('mavrk_primary_solana_wallet');
    }
  };

  // Get all available EVM addresses (embedded + external)
  const allEvmAddresses = [
    embeddedEvmWallet?.address,
    ...connectedEvmWallets.map((w: any) => w.address)
  ].filter(Boolean) as string[];

  // Get all available Solana addresses (embedded + external + local)
  const allSolanaAddresses = [
    embeddedSolanaWallet?.address,
    ...connectedSolanaWallets.map((w: any) => w.address),
    localSolanaWallet
  ].filter(Boolean) as string[];

  // Auto-select primary wallet if not set or if selected wallet was removed
  React.useEffect(() => {
    // Auto-select EVM
    if (allEvmAddresses.length > 0 && (!primaryEvmWalletAddress || !allEvmAddresses.includes(primaryEvmWalletAddress))) {
      setPrimaryEvmWallet(allEvmAddresses[0]);
    } else if (allEvmAddresses.length === 0 && primaryEvmWalletAddress) {
      setPrimaryEvmWallet(null);
    }
    // Auto-select Solana
    if (allSolanaAddresses.length > 0 && (!primarySolanaWalletAddress || !allSolanaAddresses.includes(primarySolanaWalletAddress))) {
      setPrimarySolanaWallet(allSolanaAddresses[0]);
    } else if (allSolanaAddresses.length === 0 && primarySolanaWalletAddress) {
      setPrimarySolanaWallet(null);
    }
  }, [allEvmAddresses.join(','), allSolanaAddresses.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Direct Phantom Solana connection
  const handleConnectPhantom = async () => {
    try {
      // Check multiple ways Phantom might expose its Solana provider
      const phantom = (window as any).phantom?.solana 
        || (window as any).solana;
      
      if (!phantom?.isPhantom) {
        // Check for Solflare
        const solflare = (window as any).solflare;
        if (solflare?.isSolflare) {
          console.log('🔍 Detected Solflare wallet');
          const response = await solflare.connect();
          const publicKey = response.publicKey.toString();
          console.log('✅ Solflare connected:', publicKey);
          updateLocalSolanaWallet(publicKey);
          return;
        }
        
        // No Solana wallet found - open Phantom install page
        alert('No Solana wallet detected.\n\nPlease install Phantom or Solflare browser extension and refresh the page.');
        window.open('https://phantom.app/', '_blank');
        return;
      }

      console.log('🔍 Detected Phantom wallet');

      // Connect to Phantom directly for Solana
      const response = await phantom.connect();
      const publicKey = response.publicKey.toString();
      
      console.log('✅ Phantom Solana connected:', publicKey);
      updateLocalSolanaWallet(publicKey);
      
      // Try to link with Privy (may or may not work depending on Privy's detection)
      try {
        await linkWallet();
      } catch (linkErr) {
        // Privy might not link it, but we've connected locally
        console.log('Privy link result:', linkErr);
      }
      } catch (err: any) {
      if (err.code === 4001) {
        console.log('User rejected Solana wallet connection');
      } else {
        console.error('Failed to connect Solana wallet:', err);
        alert('Failed to connect. Make sure your Solana wallet is unlocked.');
      }
    }
  };

  // Handle sign out
  const handleSignOut = () => {
    logout();
    if (isConnected) {
      disconnect();
    }
    setIsDropdownOpen(false);
  };

  // Copy address to clipboard (with fallback for unfocused documents)
  const copyToClipboard = async (address: string) => {
    try {
      if (navigator.clipboard && document.hasFocus()) {
        await navigator.clipboard.writeText(address);
      } else {
        // Fallback for unfocused documents
        const textarea = document.createElement('textarea');
        textarea.value = address;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        textarea.style.top = '0';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedAddress(address);
      setTimeout(() => setCopiedAddress(null), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Get display name for badge
  // Priority: Domain name (ENS/ZNS) → Primary wallet address → Social handles (no email for privacy)
  const getDisplayName = () => {
    // 1. If the primary EVM wallet has a domain name (ENS .eth or ZNS .ink), show that
    if (domainName) return domainName;
    
    // 2. If there's a primary EVM wallet selected, show the formatted address
    if (effectivePrimaryEvmAddress) return formatAddress(effectivePrimaryEvmAddress);
    
    // 3. Fall back to social handles (but NOT email for privacy)
    if (user?.twitter?.username) return `@${user.twitter.username}`;
    if (user?.discord?.username) return user.discord.username;
    
    // 4. Last resort - just show "Connected"
    return 'Connected';
  };

  // Show user badge if authenticated
  if (authenticated && user) {
  return (
      <UserBadgeContainer>
        <UserBadge onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <span>✓ {getDisplayName()}</span>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </UserBadge>
        
        <WalletDropdown $isOpen={isDropdownOpen}>
          <DropdownHeader>
            <DropdownTitle>Your Wallets</DropdownTitle>
            <LogoutButton onClick={handleSignOut}>Sign Out</LogoutButton>
          </DropdownHeader>
          
          {/* ===== PRIVY EMBEDDED WALLETS ===== */}
          <WalletSectionTitle>Privy Wallets (Custodied)</WalletSectionTitle>
          
          {/* Embedded EVM Wallet */}
          <WalletSection>
            <WalletLabel>
              <WalletChainIcon src="https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg" alt="EVM" />
              EVM
              <WalletTypeLabel>Smart Wallet</WalletTypeLabel>
            </WalletLabel>
            {embeddedEvmWallet?.address ? (
              <WalletAddress>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {formatAddress(embeddedEvmWallet.address)}
                  {primaryEvmWalletAddress === embeddedEvmWallet.address && <PrimaryBadge>Active</PrimaryBadge>}
                </span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PrimaryButton 
                    $isPrimary={primaryEvmWalletAddress === embeddedEvmWallet.address}
                    onClick={() => setPrimaryEvmWallet(embeddedEvmWallet.address)}
                    title={primaryEvmWalletAddress === embeddedEvmWallet.address ? 'Active wallet' : 'Set as active'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={primaryEvmWalletAddress === embeddedEvmWallet.address ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </PrimaryButton>
                  <CopyButton 
                    onClick={() => copyToClipboard(embeddedEvmWallet.address)}
                    title="Copy address"
                  >
                    {copiedAddress === embeddedEvmWallet.address ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                    )}
                  </CopyButton>
                </div>
              </WalletAddress>
            ) : (
              <ConnectWalletButton 
                onClick={() => createWallet()}
                disabled={isCreatingWallet}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                {isCreatingWallet ? 'Creating...' : 'Create Smart Wallet'}
              </ConnectWalletButton>
            )}
          </WalletSection>
          
          {/* Embedded Solana Wallet */}
          <WalletSection>
            <WalletLabel>
              <WalletChainIcon src="https://icons.llamao.fi/icons/chains/rsz_solana.jpg" alt="Solana" />
              Solana
              <WalletTypeLabel>Embedded</WalletTypeLabel>
            </WalletLabel>
            {embeddedSolanaWallet?.address ? (
              <WalletAddress>
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  {formatAddress(embeddedSolanaWallet.address)}
                  {primarySolanaWalletAddress === embeddedSolanaWallet.address && <PrimaryBadge>Active</PrimaryBadge>}
                </span>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <PrimaryButton 
                    $isPrimary={primarySolanaWalletAddress === embeddedSolanaWallet.address}
                    onClick={() => setPrimarySolanaWallet(embeddedSolanaWallet.address)}
                    title={primarySolanaWalletAddress === embeddedSolanaWallet.address ? 'Active wallet' : 'Set as active'}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={primarySolanaWalletAddress === embeddedSolanaWallet.address ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  </PrimaryButton>
                  <CopyButton 
                    onClick={() => copyToClipboard(embeddedSolanaWallet.address)}
                    title="Copy address"
                  >
                    {copiedAddress === embeddedSolanaWallet.address ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="9" y="9" width="13" height="13" rx="2" />
                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                      </svg>
                    )}
                  </CopyButton>
                </div>
              </WalletAddress>
            ) : (
              <ConnectWalletButton 
                onClick={() => (createWallet as any)({ chainType: 'solana' })}
                disabled={isCreatingWallet}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                {isCreatingWallet ? 'Creating...' : 'Create Solana Wallet'}
              </ConnectWalletButton>
            )}
          </WalletSection>

          {/* ===== CONNECTED EXTERNAL WALLETS ===== */}
          <WalletSectionTitle style={{ marginTop: '16px' }}>External Wallets (Self-Custody)</WalletSectionTitle>
          
          {/* Connected EVM Wallets - Show ALL linked wallets */}
          <WalletSection>
            <WalletLabel>
              <WalletChainIcon src="https://icons.llamao.fi/icons/chains/rsz_ethereum.jpg" alt="EVM" />
              EVM
              <WalletTypeLabel style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>External</WalletTypeLabel>
            </WalletLabel>
            {/* Show all linked EVM wallets */}
            {connectedEvmWallets.length > 0 ? (
              <>
                {connectedEvmWallets.map((wallet: any, index: number) => (
                  <WalletAddress key={wallet.address} style={{ marginBottom: index < connectedEvmWallets.length - 1 ? '8px' : '0' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {formatAddress(wallet.address)}
                      <span style={{ fontSize: '9px', color: '#71717a', textTransform: 'uppercase' }}>
                        {wallet.walletClientType || 'wallet'}
                      </span>
                      {primaryEvmWalletAddress === wallet.address && <PrimaryBadge>Active</PrimaryBadge>}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <PrimaryButton 
                        $isPrimary={primaryEvmWalletAddress === wallet.address}
                        onClick={() => setPrimaryEvmWallet(wallet.address)}
                        title={primaryEvmWalletAddress === wallet.address ? 'Active wallet' : 'Set as active'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={primaryEvmWalletAddress === wallet.address ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </PrimaryButton>
                      <CopyButton 
                        onClick={() => copyToClipboard(wallet.address)}
                        title="Copy address"
                      >
                        {copiedAddress === wallet.address ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                        )}
                      </CopyButton>
                      {/* Unlink button for this specific wallet */}
                      <CopyButton 
                        onClick={async () => {
                          try {
                            await unlinkWallet(wallet.address);
                            console.log('✅ Wallet unlinked:', wallet.address);
                          } catch (error) {
                            console.error('Failed to unlink wallet:', error);
                            disconnect();
                          }
                        }}
                        title={`Unlink ${wallet.walletClientType || 'wallet'}`}
                        style={{ marginLeft: '4px' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </CopyButton>
                    </div>
                  </WalletAddress>
                ))}
                {/* Always show connect button to add more wallets */}
                <ConnectWalletButton onClick={handleConnectExternalWallet} style={{ marginTop: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Connect Another Wallet
                </ConnectWalletButton>
              </>
            ) : (
              <ConnectWalletButton onClick={handleConnectExternalWallet}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Connect MetaMask / WalletConnect
              </ConnectWalletButton>
            )}
          </WalletSection>
          
          {/* Connected Solana Wallets - Show ALL linked wallets */}
          <WalletSection>
            <WalletLabel>
              <WalletChainIcon src="https://icons.llamao.fi/icons/chains/rsz_solana.jpg" alt="Solana" />
              Solana
              <WalletTypeLabel style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>External</WalletTypeLabel>
            </WalletLabel>
            {connectedSolanaWallets.length > 0 || localSolanaWallet ? (
              <>
                {/* Show all Privy-linked Solana wallets */}
                {connectedSolanaWallets.map((wallet: any, index: number) => (
                  <WalletAddress key={wallet.address} style={{ marginBottom: '8px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {formatAddress(wallet.address)}
                      <span style={{ fontSize: '9px', color: '#71717a', textTransform: 'uppercase' }}>
                        {wallet.walletClientType || 'wallet'}
                      </span>
                      {primarySolanaWalletAddress === wallet.address && <PrimaryBadge>Active</PrimaryBadge>}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <PrimaryButton 
                        $isPrimary={primarySolanaWalletAddress === wallet.address}
                        onClick={() => setPrimarySolanaWallet(wallet.address)}
                        title={primarySolanaWalletAddress === wallet.address ? 'Active wallet' : 'Set as active'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={primarySolanaWalletAddress === wallet.address ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </PrimaryButton>
                      <CopyButton 
                        onClick={() => copyToClipboard(wallet.address)}
                        title="Copy address"
                      >
                        {copiedAddress === wallet.address ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                        )}
                      </CopyButton>
                      {/* Unlink button */}
                      <CopyButton 
                        onClick={async () => {
                          try {
                            await unlinkWallet(wallet.address);
                            console.log('✅ Solana wallet unlinked:', wallet.address);
                          } catch (error) {
                            console.error('Failed to unlink wallet:', error);
                          }
                        }}
                        title={`Unlink ${wallet.walletClientType || 'wallet'}`}
                        style={{ marginLeft: '4px' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </CopyButton>
                    </div>
                  </WalletAddress>
                ))}
                {/* Show locally connected wallet if not already in Privy list */}
                {localSolanaWallet && !connectedSolanaWallets.find((w: any) => w.address === localSolanaWallet) && (
                  <WalletAddress style={{ marginBottom: '8px' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {formatAddress(localSolanaWallet)}
                      <span style={{ fontSize: '9px', color: '#f59e0b', textTransform: 'uppercase' }}>local</span>
                      {primarySolanaWalletAddress === localSolanaWallet && <PrimaryBadge>Active</PrimaryBadge>}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <PrimaryButton 
                        $isPrimary={primarySolanaWalletAddress === localSolanaWallet}
                        onClick={() => setPrimarySolanaWallet(localSolanaWallet)}
                        title={primarySolanaWalletAddress === localSolanaWallet ? 'Active wallet' : 'Set as active'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill={primarySolanaWalletAddress === localSolanaWallet ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                        </svg>
                      </PrimaryButton>
                      <CopyButton 
                        onClick={() => copyToClipboard(localSolanaWallet)}
                        title="Copy address"
                      >
                        {copiedAddress === localSolanaWallet ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                        )}
                      </CopyButton>
                      <CopyButton 
                        onClick={() => {
                          updateLocalSolanaWallet(null);
                          const phantom = (window as any).phantom?.solana || (window as any).solana;
                          phantom?.disconnect?.();
                        }}
                        title="Disconnect wallet"
                        style={{ marginLeft: '4px' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </CopyButton>
                    </div>
                  </WalletAddress>
                )}
                {/* Always show connect button */}
                <ConnectWalletButton onClick={handleConnectPhantom} style={{ marginTop: '8px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                  Connect Another Solana Wallet
                </ConnectWalletButton>
              </>
            ) : (
              <ConnectWalletButton onClick={handleConnectPhantom}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Connect Phantom / Solflare
              </ConnectWalletButton>
            )}
          </WalletSection>

          {/* ===== BEHOLDER (KRAKEN) GLOBAL WALLET ===== */}
          {BEHOLDER_APP_ID && (
            <>
              <WalletSectionTitle style={{ marginTop: '16px' }}>Beholder Wallet (Kraken)</WalletSectionTitle>
              <WalletSection>
                <WalletLabel>
                  <WalletChainIcon src="https://beholder.kraken.com/favicon.ico" alt="Beholder" />
                  Beholder
                  <WalletTypeLabel style={{ background: 'rgba(114, 137, 218, 0.15)', color: '#7289da' }}>Global</WalletTypeLabel>
                </WalletLabel>
                {beholderAccount ? (
                  <WalletAddress>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {formatAddress(beholderAccount.embeddedWallets?.[0]?.address || beholderAccount.subject || 'Linked')}
                      <span style={{ fontSize: '9px', color: '#7289da', textTransform: 'uppercase' }}>
                        connected
                      </span>
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {beholderAccount.embeddedWallets?.[0]?.address && (
                        <CopyButton 
                          onClick={() => copyToClipboard(beholderAccount.embeddedWallets[0].address)}
                          title="Copy address"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="9" y="9" width="13" height="13" rx="2" />
                            <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                          </svg>
                        </CopyButton>
                      )}
                      {/* Unlink Beholder button */}
                      <CopyButton 
                        onClick={handleUnlinkBeholder}
                        title="Unlink Beholder wallet"
                        style={{ marginLeft: '4px' }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </CopyButton>
                    </div>
                  </WalletAddress>
                ) : (
                  <ConnectWalletButton 
                    onClick={handleLinkBeholder}
                    disabled={isLinkingBeholder}
                    style={{ 
                      background: 'rgba(114, 137, 218, 0.1)', 
                      borderColor: '#7289da',
                      color: '#7289da'
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    {isLinkingBeholder ? 'Connecting...' : 'Connect Beholder Wallet'}
                  </ConnectWalletButton>
                )}
              </WalletSection>
            </>
          )}
          
          {/* User Info (if social login) */}
          {(user.email || user.google || user.twitter || user.discord) && (
            <UserInfo>
              {user.email?.address && (
                <UserInfoRow>
                  <span>Email</span>
                  <span>{user.email.address}</span>
                </UserInfoRow>
              )}
              {user.google?.email && (
                <UserInfoRow>
                  <span>Google</span>
                  <span>{user.google.email}</span>
                </UserInfoRow>
              )}
              {user.twitter?.username && (
                <UserInfoRow>
                  <span>Twitter</span>
                  <span>@{user.twitter.username}</span>
                </UserInfoRow>
              )}
              {user.discord?.username && (
                <UserInfoRow>
                  <span>Discord</span>
                  <span>{user.discord.username}</span>
                </UserInfoRow>
              )}
            </UserInfo>
          )}
        </WalletDropdown>
        
        {/* Click outside to close */}
        {isDropdownOpen && (
          <div 
        style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999,
            }}
            onClick={() => setIsDropdownOpen(false)}
          />
        )}
      </UserBadgeContainer>
    );
  }

  // Show Sign In button
  return (
    <SignInButton onClick={handleLogin} disabled={!ready}>
      {ready ? 'Sign In' : 'Loading...'}
    </SignInButton>
  );
};

export default WalletConnect;
